import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { SessionPoint, SubagentSummary } from '@ace-tracker/shared';
import { CHART_SERIES_COLORS, chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { SESSIONS_METRIC_INFO } from '../metricInfo';
import { ChartCard } from './ChartCard';
import { TOOL_LABELS } from './Sidebar';
import { TableCard } from './TableCard';

const DASH = '—';

function share(part: number, whole: number): string {
  return whole > 0 ? `${((part / whole) * 100).toFixed(part / whole < 0.1 && part > 0 ? 1 : 0)}%` : DASH;
}

/**
 * Subagent analytics for one tool (E1.14, E2.6): subagents' share of cost, cost
 * per run, and the costliest runs. A run opens its parent session's detail when
 * that session is in the list above.
 */
export function SubagentSection({
  subagents,
  sessions,
  money,
  onOpenSession,
  SectionHeading,
}: {
  subagents: SubagentSummary;
  sessions: SessionPoint[];
  money: (value: number) => string;
  onOpenSession: (session: SessionPoint) => void;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const total = subagents.rootCost + subagents.subagentCost;
  const toolLabel = TOOL_LABELS[subagents.tool];
  const toolSessions = new Map(sessions.filter((s) => s.tool === subagents.tool).map((s) => [s.sessionId, s]));

  return (
    <section>
      <SectionHeading
        title={`${toolLabel} subagents`}
        hint={
          subagents.tool === 'claude-code'
            ? 'Turns from subagent runs, next to the root sessions that started them · records from collector 1.6.0 or later'
            : 'Turns from spawned subagent threads, next to the root sessions that started them'
        }
        info={SESSIONS_METRIC_INFO.subagents}
      />
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Tile label={`Share of ${toolLabel} cost`} value={share(subagents.subagentCost, total)} meta={`${money(subagents.subagentCost)} of ${money(total)}`} />
        <Tile
          label="Subagent runs"
          value={subagents.runs.toLocaleString()}
          meta={`${subagents.sessionsWithSubagents} of ${subagents.sessions} sessions used them`}
        />
        <Tile
          label="Cost per run"
          value={subagents.costPerRun === null ? DASH : money(subagents.costPerRun)}
          meta={subagents.medianRunCost === null ? 'No runs in range' : `Median ${money(subagents.medianRunCost)}`}
        />
        <Tile
          label="Share of requests"
          value={share(subagents.subagentRequests, subagents.rootRequests + subagents.subagentRequests)}
          meta={`${subagents.subagentRequests.toLocaleString()} subagent requests`}
        />
      </div>

      {subagents.runs > 0 && (
        <>
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <ChartCard title={`${toolLabel} cost per day, root vs subagent`} caption="Indicative cost per UTC day" info={SESSIONS_METRIC_INFO.subagents}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={subagents.daily} margin={{ left: 8, right: 12, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" vertical={false} />
                  <XAxis dataKey="date" {...chartXAxisTickProps} />
                  <YAxis {...chartAxisTickProps} width={64} tickFormatter={(v: number) => money(v)} />
                  <Tooltip {...chartTooltipProps} formatter={((value: number) => money(value)) as never} />
                  <Legend {...chartLegendProps} />
                  <Bar dataKey="rootCost" name="Root session" stackId="cost" fill={CHART_SERIES_COLORS[0]} isAnimationActive={false} />
                  <Bar dataKey="subagentCost" name="Subagents" stackId="cost" fill={CHART_SERIES_COLORS[1]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <TableCard title={`${toolLabel} subagent cost by model`} info={SESSIONS_METRIC_INFO.subagents} empty={subagents.byModel.length === 0} emptyText="No subagent runs.">
              <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                <tr>
                  <th className="pb-2.5 pr-3">Model</th>
                  <th className="pb-2.5 pr-3 text-right">Runs</th>
                  <th className="pb-2.5 pr-3 text-right">Requests</th>
                  <th className="pb-2.5 pr-3 text-right">Cost</th>
                  <th className="pb-2.5 text-right">Cost per run</th>
                </tr>
              </thead>
              <tbody>
                {subagents.byModel.map((m) => (
                  <tr key={m.model} className="border-b border-border last:border-0">
                    <td className="py-2.5 pr-3 text-text-default">{m.model}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{m.runs.toLocaleString()}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{m.requests.toLocaleString()}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{money(m.cost)}</td>
                    <td className="py-2.5 text-right tabular-nums">{m.runs > 0 ? money(m.cost / m.runs) : DASH}</td>
                  </tr>
                ))}
              </tbody>
            </TableCard>
          </div>

          <div className="mt-5">
            <TableCard title={`Costliest ${toolLabel} subagent runs`} info={SESSIONS_METRIC_INFO.subagentRuns} empty={subagents.topRuns.length === 0} emptyText="No subagent runs.">
              <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                <tr>
                  <th className="pb-2.5 pr-3">Run</th>
                  <th className="pb-2.5 pr-3">Session</th>
                  <th className="pb-2.5 pr-3">Models</th>
                  <th className="pb-2.5 pr-3">Started</th>
                  <th className="pb-2.5 pr-3 text-right">Requests</th>
                  <th className="pb-2.5 text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {subagents.topRuns.map((run) => {
                  const parent = toolSessions.get(run.sessionId);
                  return (
                    <tr key={`${run.sessionId}:${run.threadId}`} className="border-b border-border last:border-0">
                      <td className="py-2 pr-3 font-mono text-xs text-text-muted">{run.threadId.slice(0, 8) || DASH}</td>
                      <td className="py-2 pr-3">
                        {parent ? (
                          <button
                            type="button"
                            onClick={() => onOpenSession(parent)}
                            aria-label={`Show details of session ${run.sessionId}`}
                            className="min-h-[36px] font-mono text-xs text-primary-blue underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue"
                          >
                            {run.sessionId.slice(0, 8)}
                          </button>
                        ) : (
                          <span className="font-mono text-xs text-text-muted" title="Not among the sessions listed above">
                            {run.sessionId.slice(0, 8)}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-text-muted">{run.models.join(', ')}</td>
                      <td className="py-2 pr-3 tabular-nums text-text-muted">{new Date(run.firstSeen).toLocaleString()}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{run.requests.toLocaleString()}</td>
                      <td className="py-2 text-right font-medium tabular-nums text-text-default">{money(run.cost)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </TableCard>
          </div>
        </>
      )}
    </section>
  );
}

function Tile({ label, value, meta }: { label: string; value: string; meta: string }) {
  return (
    <div className="relative rounded-card border border-border bg-surface p-4 shadow-sm">
      <span className="absolute inset-y-0 left-0 w-[3px] rounded-l-card" style={{ backgroundColor: CHART_SERIES_COLORS[1] }} />
      <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-text-default">{value}</p>
      <p className="text-xs text-text-muted">{meta}</p>
    </div>
  );
}
